import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Settings, User, Shield, Bell, Database, Palette, 
  School, Edit, Layout, CreditCard, ArrowLeft, ChevronRight, ClipboardList
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import Header from "./Header";
import AdminSidebar from "./Sidebar";
import { HeaderDesigner } from "./Headerfiles/HeaderDesigner";
import { HeaderSettings, INITIAL_HEADER_SETTINGS } from "./Headerfiles/types";
import SubscriptionManagement from "./SubscriptionManagement";

interface SettingsContentProps {
  onNavigateToSchoolRegistration?: () => void;
  onNavigateToEditSchool?: () => void;
}

const SettingsContent = ({
  onNavigateToSchoolRegistration,
  onNavigateToEditSchool
}: SettingsContentProps) => {
  const navigate = useNavigate();
  const [showHeaderDesigner, setShowHeaderDesigner] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [headerSettings, setHeaderSettings] = useState<HeaderSettings>(INITIAL_HEADER_SETTINGS);

  if (showHeaderDesigner) {
      return (
        <SidebarProvider>
            <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
                <AdminSidebar onSectionChange={() => {}} />
                <div className="flex flex-col flex-1 h-full">
                    <Header />
                    <main className="flex-1 overflow-hidden">
                         <div className="h-full flex flex-col">
                            <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 flex items-center gap-4 shadow-sm">
                                <Button variant="ghost" className="border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => setShowHeaderDesigner(false)}>
                                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                                </Button>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Header Designer</h2>
                            </div>
                            <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900/50">
                                <HeaderDesigner 
                                    settings={headerSettings} 
                                    onUpdate={setHeaderSettings}
                                    onClose={() => setShowHeaderDesigner(false)}
                                />
                            </div>
                         </div>
                    </main>
                </div>
            </div>
        </SidebarProvider>
      );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <Header />
          <main className="flex-1 overflow-y-auto">
            <div className="container mx-auto max-w-6xl p-6 space-y-8">
              
              {showSubscription ? (
                 // Subscription View
                 <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex items-center gap-4">
                      <Button variant="ghost" size="sm" onClick={() => setShowSubscription(false)} className="gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700">
                        <ArrowLeft className="h-4 w-4" />
                        Back to Settings
                      </Button>
                    </div>
                    <SubscriptionManagement />
                 </div>
              ) : (
                // Main Settings View
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    
                    {/* Page Header */}
                    <div className="flex flex-col gap-1">
                        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Settings</h1>
                        <p className="text-gray-500 dark:text-gray-400">Manage your school profile, preferences, and system configurations.</p>
                    </div>

                    <Separator className="bg-gray-200 dark:bg-gray-800" />

                    {/* School Profile Section */}
                    <section className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">School Profile</h2>
                            <Badge variant="outline" className="font-normal border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-400">Primary Configuration</Badge>
                        </div>
                        <Card className="border-l-4 border-l-blue-600 shadow-sm hover:shadow-md transition-all duration-200 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                            <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                                <div className="flex items-center gap-5">
                                    <div className="h-16 w-16 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center shrink-0 border border-blue-100 dark:border-blue-800">
                                        <School className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="font-medium text-lg text-gray-900 dark:text-gray-100">School Registration & Details</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
                                            Update your school's official information, contact details, and branding.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                                    <Button 
                                        onClick={onNavigateToSchoolRegistration} 
                                        variant="default"
                                        className="gap-2 shadow-sm bg-blue-600 hover:bg-blue-700 text-white border-0"
                                    >
                                        Register New
                                    </Button>
                                    <Button 
                                        onClick={onNavigateToEditSchool} 
                                        variant="outline"
                                        className="gap-2 border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                                    >
                                        <Edit className="h-4 w-4" />
                                        Edit Details
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* General Settings Grid */}
                    <section className="space-y-4">
                        <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">General Configuration</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            
                            {/* Subscription Card */}
                            <SettingsCard 
                                icon={CreditCard}
                                title="Subscription & Billing"
                                description="Manage your plan, invoices, and payments."
                                color="text-emerald-600 dark:text-emerald-400"
                                bgColor="bg-emerald-50 dark:bg-emerald-900/20"
                                onClick={() => setShowSubscription(true)}
                                actionLabel="Manage Subscription"
                            />

                            {/* Registration Management */}
                            <SettingsCard 
                                icon={ClipboardList}
                                title="Registration Management"
                                description="Manage registration records and settings."
                                color="text-orange-600 dark:text-orange-400"
                                bgColor="bg-orange-50 dark:bg-orange-900/20"
                                onClick={() => navigate('/dashboard/registration-management')}
                                actionLabel="Open Manager"
                            />

                            {/* Header Settings */}
                            <SettingsCard 
                                icon={Layout}
                                title="Header Customization"
                                description="Design and adjust your school's letterhead."
                                color="text-indigo-600 dark:text-indigo-400"
                                bgColor="bg-indigo-50 dark:bg-indigo-900/20"
                                onClick={() => setShowHeaderDesigner(true)}
                                actionLabel="Open Designer"
                            />

                            {/* Appearance */}
                            <SettingsCard 
                                icon={Palette}
                                title="Appearance"
                                description="Themes, colors, and visual preferences."
                                color="text-purple-600 dark:text-purple-400"
                                bgColor="bg-purple-50 dark:bg-purple-900/20"
                                onClick={() => {}}
                                actionLabel="Customize"
                            />

                             {/* User Management */}
                             <SettingsCard 
                                icon={User}
                                title="User Management"
                                description="Accounts, roles, and access control."
                                color="text-blue-600 dark:text-blue-400"
                                bgColor="bg-blue-50 dark:bg-blue-900/20"
                                onClick={() => {}}
                                actionLabel="Manage Users"
                            />

                            {/* Security */}
                            <SettingsCard 
                                icon={Shield}
                                title="Security"
                                description="Password policies and 2FA settings."
                                color="text-red-600 dark:text-red-400"
                                bgColor="bg-red-50 dark:bg-red-900/20"
                                onClick={() => {}}
                                actionLabel="Configure"
                            />

                            {/* Notifications */}
                            <SettingsCard 
                                icon={Bell}
                                title="Notifications"
                                description="Email alerts and push notifications."
                                color="text-amber-600 dark:text-amber-400"
                                bgColor="bg-amber-50 dark:bg-amber-900/20"
                                onClick={() => {}}
                                actionLabel="Setup Alerts"
                            />

                             {/* Data Management */}
                             <SettingsCard 
                                icon={Database}
                                title="Data & Backup"
                                description="Import, export, and backup your data."
                                color="text-teal-600 dark:text-teal-400"
                                bgColor="bg-teal-50 dark:bg-teal-900/20"
                                onClick={() => {}}
                                actionLabel="Manage Data"
                            />

                             {/* System Settings */}
                             <SettingsCard 
                                icon={Settings}
                                title="System Preferences"
                                description="Academic years and grade levels."
                                color="text-gray-600 dark:text-gray-400"
                                bgColor="bg-gray-100 dark:bg-gray-800"
                                onClick={() => {}}
                                actionLabel="System Config"
                            />

                        </div>
                    </section>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

// Helper Component for Cards
const SettingsCard = ({ icon: Icon, title, description, color, bgColor, onClick, actionLabel }: any) => (
    <Card className="group hover:shadow-md transition-all duration-200 cursor-pointer shadow-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700" onClick={onClick}>
        <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-xl ${bgColor}`}>
                    <Icon className={`h-6 w-6 ${color}`} />
                </div>
                <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-gray-500 transition-colors" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 group-hover:text-blue-600 transition-colors">{title}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">{description}</p>
            <div className="text-xs font-medium text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                {actionLabel} <ChevronRight className="h-3 w-3" />
            </div>
        </CardContent>
    </Card>
);

export default SettingsContent;